import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Popover,
  Typography,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import { useState } from "react";
import { MAX_FROZEN_COLUMNS } from "../../../utils/frozenColumns";

export type FreezableColumn = {
  key: string;
  label: string;
};

type Props = {
  columns: FreezableColumn[];
  frozenKeys: string[];
  isAtMax: boolean;
  onToggle: (key: string) => void;
  onClear: () => void;
};

const FreezeColumnsPopover = ({
  columns,
  frozenKeys,
  isAtMax,
  onToggle,
  onClear,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          height: "48px",
          width: "48px",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
        }}
        aria-label="Freeze columns"
      >
        <Badge
          badgeContent={frozenKeys.length || null}
          color="primary"
          overlap="circular"
        >
          <PushPinIcon
            color={frozenKeys.length > 0 ? "primary" : "disabled"}
            sx={{ fontSize: "24px" }}
          />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
            Freeze columns
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Pin up to {MAX_FROZEN_COLUMNS} columns while scrolling horizontally.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              maxHeight: 280,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {columns.map((column) => {
              const checked = frozenKeys.includes(column.key);
              const disabled = !checked && isAtMax;

              return (
                <FormControlLabel
                  key={column.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => onToggle(column.key)}
                    />
                  }
                  label={
                    <Typography variant="body2">{column.label}</Typography>
                  }
                />
              );
            })}
          </Box>

          {isAtMax && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1 }}>
              Maximum {MAX_FROZEN_COLUMNS} columns can be frozen. Unfreeze one
              to select another.
            </Typography>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              size="small"
              onClick={onClear}
              disabled={frozenKeys.length === 0}
            >
              Clear all
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default FreezeColumnsPopover;
